/**
 * ResultScreen2.tsx - Results display for the TV/EnergieLabel decision of the Nudging experiment
 * Last updated: 2025-08-18
 * Updated by: JulianPolly
 */

import React, { useEffect, useState, useMemo } from "react";
import { Bar, BarChart, Tooltip, XAxis, YAxis, ResponsiveContainer, LabelList, Cell } from "recharts";
import { useSession } from "../../../SessionContext";
import { ScreenProps } from "../../SequenceController";
import { NudgingKey } from "./NudgingSequence";
import { useLanguage } from "../../../../context/LanguageContext";
import { colors, commonStyles, chartColors } from "../../../../components/colors";
import TVBAplusImg from "./assets/TVB_Aplus.png";
import TVBDImg from "./assets/TVB_D.png";

const cardStyle: React.CSSProperties = {
    ...commonStyles.card,
    margin: 0,
    minWidth: 800,
    maxWidth: 1200,
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    minHeight: 340,
    height: 420,
    padding: 32,
    justifyContent: "flex-start"
};

const chartColumnStyle: React.CSSProperties = {
    width: "70%",
    display: "flex",
    flexDirection: "column",
    gap: 8
};

const resultMessageStyle: React.CSSProperties = {
    ...commonStyles.card,
    marginTop: 32,
    textAlign: "center",
    fontSize: 17,
    background: colors.backgroundLight,
    maxWidth: 650,
    marginLeft: "auto",
    marginRight: "auto",
};

const chartBlockStyle: React.CSSProperties = {
    display: "flex",
    flexDirection: "row",
    gap: 24,
    justifyContent: "space-between",
    alignItems: "center",
    flex: 1,
    minWidth: 450,
    maxWidth: 750,
    height: 350
};

const chartStyle: React.CSSProperties = {
    minWidth: 430,
    maxWidth: 700,
    flex: 1,
    height: 350
};

const imageStyle: React.CSSProperties = {
    width: 180,
    height: 180,
    objectFit: "contain",
    filter: "drop-shadow(0 2px 12px #8883)",
    marginLeft: 24
};

const primaryColor = chartColors.primary[1] || "#238551";
const secondaryColor = "#D7263D";

// Custom Tooltip with percentages
const CustomTooltip = ({ active, payload, label, total }: any) => {
    if (active && payload && payload.length) {
        const value = payload[0].value;
        const percentage = total > 0 ? Math.round((value / total) * 100) : 0;
        return (
            <div style={{
                background: "#fff",
                border: `1px solid ${colors.silver}`,
                padding: 12,
                borderRadius: 8,
                fontSize: 14,
                boxShadow: "0 2px 8px rgba(0,0,0,0.06)"
            }}>
                <div style={{
                    fontWeight: 600,
                    marginBottom: 6,
                    color: colors.brillantBlue
                }}>
                    {label}
                </div>
                <div style={{ color: payload[0].color || colors.textMedium }}>
                    {value} ({percentage}%)
                </div>
            </div>
        );
    }
    return null;
};

export function ResultScreen2(props: ScreenProps) {
    const { getGroupData, userData } = useSession();
    const { language } = useLanguage();
    const [chart1Data, setChart1Data] = useState<any[]>([]);
    const [chart2Data, setChart2Data] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);
    const [total1, setTotal1] = useState(0);
    const [total2, setTotal2] = useState(0);

    // Chart data/labels for TV screens
    const tvNoLabel = language === "en" ? "TV with EnergyStar" : "TV mit Energie-Label";
    const tvAplus = language === "en" ? "TV with A+ Label" : "TV mit A+ Label";
    const tvD = language === "en" ? "TV with D Label" : "TV mit D-Label";

    // X-axis labels for chart1 and chart2
    const chart1Labels = useMemo(() => [tvNoLabel, tvAplus], [tvNoLabel, tvAplus]);
    const chart2Labels = useMemo(() => [tvNoLabel, tvD], [tvNoLabel, tvD]);

    const resultsTitle = language === "en"
        ? "TV Choice Results"
        : "Ergebnisse der TV-Auswahl";
    const resultsDesc = language === "en"
        ? "See here how your class chose between the two TVs, depending on which energy label was shown."
        : "Hier siehst du, wie sich deine Klasse je nach Energie-Label für eines der beiden TVs entschieden hat.";

    // Info sentence logic
    let ownTV: string | null = null;
    let ownGroup: string | null = null;
    let ownGroupLabel: string;
    let ownFraming: string;
    if (userData && userData[NudgingKey]) {
        ownGroup =
            userData?.nudgeGroup ||
            userData?.randomizer2 ||
            (userData[NudgingKey][0] ? "Gruppe 1" : userData[NudgingKey][1] ? "Gruppe 2" : "");
        // Find user's TV choice for each group
        const choice1 = userData[NudgingKey][0]?.selections?.[0]?.choice;
        const choice2 = userData[NudgingKey][1]?.selections?.[0]?.choice;
        if (choice1 !== undefined && choice1 !== null) ownTV = chart1Labels[choice1];
        if (choice2 !== undefined && choice2 !== null) ownTV = chart2Labels[choice2];
    }
    if (ownGroup === "goodFirst" || ownGroup === "Bedingung 1" || ownGroup === "Gruppe 1") {
        ownGroupLabel = language === "en" ? "Group 1" : "Gruppe 1";
        ownFraming = language === "en" ? "with A+ label" : "mit A+ Label";
    } else {
        ownGroupLabel = language === "en" ? "Group 2" : "Gruppe 2";
        ownFraming = language === "en" ? "with D label" : "mit D-Label";
    }
    const infoText = language === "en"
        ? <>You were randomly assigned to <b>{ownGroupLabel}</b> and saw a choice between a TV <b>with EnergyStar</b> and one <b>{ownFraming}</b>.<br />
            Your choice: {ownTV ? <b>{ownTV}</b> : <i>not detected</i>}.</>
        : <>Du warst zufällig in <b>{ownGroupLabel}</b> und hast die Auswahl zwischen einem TV <b>mit Energie-Label</b> und einem <b>{ownFraming}</b> gesehen.<br />
            Deine Wahl: {ownTV ? <b>{ownTV}</b> : <i>nicht erkannt</i>}.</>;

    useEffect(() => {
        setLoading(true);
        getGroupData().then(data => {
            let group1 = [0, 0]; // [EnergyStar, A+]
            let group2 = [0, 0]; // [EnergyStar, D]
            data?.forEach((dat: any) => {
                // Gruppenzuordnung suchen
                let group = "";
                if (dat.nudgeGroup) {
                    group = dat.nudgeGroup;
                } else if (dat.randomizer2) {
                    group = dat.randomizer2;
                } else if (dat[NudgingKey]?.[0]?.condition === "goodFirst") {
                    group = "goodFirst";
                } else if (dat[NudgingKey]?.[1]?.condition === "badFirst") {
                    group = "badFirst";
                }
                // Gruppe 1 (TVgood): Wert aus [0], Gruppe 2 (TVbad): Wert aus [1]
                if (
                    group === "goodFirst" ||
                    group === "Bedingung 1" ||
                    group === "Gruppe 1"
                ) {
                    const choice = dat[NudgingKey]?.[0]?.selections?.[0]?.choice;
                    if (choice === 0) group1[0]++;
                    if (choice === 1) group1[1]++;
                } else if (
                    group === "badFirst" ||
                    group === "Bedingung 2" ||
                    group === "Gruppe 2"
                ) {
                    const choice = dat[NudgingKey]?.[1]?.selections?.[0]?.choice;
                    if (choice === 0) group2[0]++;
                    if (choice === 1) group2[1]++;
                }
            });

            setChart1Data([
                { name: chart1Labels[0], Anzahl: group1[0], color: primaryColor },
                { name: chart1Labels[1], Anzahl: group1[1], color: secondaryColor }
            ]);
            setChart2Data([
                { name: chart2Labels[0], Anzahl: group2[0], color: primaryColor },
                { name: chart2Labels[1], Anzahl: group2[1], color: secondaryColor }
            ]);
            setTotal1(group1[0] + group1[1]);
            setTotal2(group2[0] + group2[1]);
            setLoading(false);
        }).catch(error => {
            setLoading(false);
            console.error("Error fetching group data:", error);
        });
    }, [getGroupData, chart1Labels, chart2Labels]);

    if (loading) {
        return <div style={{
            textAlign: "center",
            marginTop: 50,
            fontSize: 17,
            color: colors.brillantBlue
        }}>
            {language === "en" ? "Loading results..." : "Lade Ergebnisse…"}
        </div>;
    }

    return (
        <div className="experiment-container" style={commonStyles.container}>
            <h2 style={{
                ...commonStyles.heading,
                margin: "0 0 24px 0",
                fontSize: 28,
                fontWeight: 700
            }}>
                {resultsTitle}
            </h2>
            <div style={{
                fontSize: 16,
                color: colors.textMedium,
                marginBottom: 24,
                textAlign: "center"
            }}>
                {resultsDesc}
            </div>
            <div style={{
                display: "flex",
                flexDirection: "row",
                alignItems: "flex-start",
                justifyContent: "center",
                gap: 48,
                marginTop: 28,
                flexWrap: "wrap"
            }}>
                {/* Chart 1 - A+ Label */}
                <div style={cardStyle}>
                    <div style={chartColumnStyle}>
                        <h3 style={{
                            ...commonStyles.heading,
                            fontSize: 20,
                            marginBottom: 12
                        }}>
                            {language === "en"
                                ? "Group 1: TV with A+ Label"
                                : "Gruppe 1: TV mit A+ Label"}
                        </h3>
                        <div style={chartBlockStyle}>
                            <div style={chartStyle}>
                                <ResponsiveContainer width="100%" height={350}>
                                    <BarChart data={chart1Data}>
                                        <XAxis
                                            dataKey="name"
                                            interval={0}
                                            height={70}
                                            tick={{ fontSize: 16, angle: 0, textAnchor: "middle" } as any}
                                        />
                                        <YAxis allowDecimals={false} domain={[0, 'dataMax + 1']} />
                                        <Tooltip
                                            content={<CustomTooltip total={total1} />}
                                            cursor={{ fill: 'rgba(200, 200, 200, 0.2)' }}
                                        />
                                        <Bar dataKey="Anzahl" barSize={70}>
                                            {chart1Data.map((entry) => (
                                                <Cell key={entry.name} fill={entry.color} />
                                            ))}
                                            <LabelList
                                                dataKey="Anzahl"
                                                position="top"
                                                fontSize={16}
                                                fill={colors.brillantBlue}
                                                fontWeight={600}
                                            />
                                        </Bar>
                                    </BarChart>
                                </ResponsiveContainer>
                            </div>
                        </div>
                    </div>
                    <img
                        src={TVBAplusImg}
                        alt={language === "en" ? "TV with A+ label" : "TV mit A+ Label"}
                        style={imageStyle}
                    />
                </div>
                {/* Chart 2 - D Label */}
                <div style={cardStyle}>
                    <div style={chartColumnStyle}>
                        <h3 style={{
                            ...commonStyles.heading,
                            fontSize: 20,
                            marginBottom: 12
                        }}>
                            {language === "en"
                                ? "Group 2: TV with D Label"
                                : "Gruppe 2: TV mit D-Label"}
                        </h3>
                        <div style={chartBlockStyle}>
                            <div style={chartStyle}>
                                <ResponsiveContainer width="100%" height={350}>
                                    <BarChart data={chart2Data}>
                                        <XAxis
                                            dataKey="name"
                                            interval={0}
                                            height={70}
                                            tick={{ fontSize: 16, angle: 0, textAnchor: "middle" } as any}
                                        />
                                        <YAxis allowDecimals={false} domain={[0, 'dataMax + 1']} />
                                        <Tooltip
                                            content={<CustomTooltip total={total2} />}
                                            cursor={{ fill: 'rgba(200, 200, 200, 0.2)' }}
                                        />
                                        <Bar dataKey="Anzahl" barSize={70}>
                                            {chart2Data.map((entry) => (
                                                <Cell key={entry.name} fill={entry.color} />
                                            ))}
                                            <LabelList
                                                dataKey="Anzahl"
                                                position="top"
                                                fontSize={16}
                                                fill={colors.brillantBlue}
                                                fontWeight={600}
                                            />
                                        </Bar>
                                    </BarChart>
                                </ResponsiveContainer>
                            </div>
                        </div>
                    </div>
                    <img
                        src={TVBDImg}
                        alt={language === "en" ? "TV with D label" : "TV mit D-Label"}
                        style={imageStyle}
                    />
                </div>
            </div>
            <div
                style={resultMessageStyle}
            >
                {infoText}
            </div>
        </div>
    );
}